import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { dashboardAPI, timeTrackingAPI, changeRequestsAPI, adminAPI } from '../lib/api';
import { toast } from 'sonner';
import { useDashboardSSE } from '../hooks/useDashboardSSE';
import {
  Users, Building2, Calendar, CalendarDays, Clock, ArrowRight,
  CheckCircle, XCircle, AlertCircle, Wrench,
} from 'lucide-react';
import { format, parseISO, startOfWeek, endOfWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import { TimeTrackingWidget } from './dashboard/TimeTrackingWidget';
import { MyShiftsWidget } from './dashboard/MyShiftsWidget';
import { ImportantNewsWidget } from './dashboard/ImportantNewsWidget';
import { BirthdaysWidget } from './dashboard/BirthdaysWidget';
import { FleetSummaryWidget } from './dashboard/FleetSummaryWidget';
import { FacilitySummaryWidget } from './dashboard/FacilitySummaryWidget';
import { MyFacilityReportsWidget } from './dashboard/MyFacilityReportsWidget';
import { QMBSummaryWidget } from './dashboard/QMBSummaryWidget';
import { ExpiringContractsWidget } from './dashboard/ExpiringContractsWidget';
import { PendingSignaturesWidget } from './dashboard/PendingSignaturesWidget';
import { PendingTasksWidget } from '../components/dashboard/PendingTasksWidget';
import { EmployeeHoverCard } from '../components/EmployeeHoverCard';
import {
  AttendanceWidget, AbsencesTodayWidget, CorrectionsWidget,
  ChangeRequestsWidget, HolidaysWidget, PendingRequestsWidget,
} from './dashboard/DashboardWidgets';

export default function Dashboard() {
  const { user, isAdmin, hasPermission } = useAuth();
  // Granulare Manager-Sicht: Wer mindestens Mitarbeiter-View hat, sieht Team-Widgets
  const canViewTeam = isAdmin || hasPermission('employees', 'view');
  const canManageVacations = isAdmin || hasPermission('vacations', 'edit');
  const canViewVacations = isAdmin || hasPermission('vacations', 'view');
  const canViewAbsences = isAdmin || hasPermission('absences', 'view');
  const canViewTimeTracking = isAdmin || hasPermission('time_tracking', 'view');
  const canViewShiftsManager = isAdmin || hasPermission('shifts', 'edit');
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeStatus, setTimeStatus] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // My Shifts (Employee view)
  const [myShifts, setMyShifts] = useState([]);
  const [shiftsWeek, setShiftsWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [shiftsLoading, setShiftsLoading] = useState(false);
  
  // Change Requests (HR/Admin view)
  const [pendingChangeRequests, setPendingChangeRequests] = useState(0);

  // Consistency Check (Admin view)
  const [consistencyIssueCount, setConsistencyIssueCount] = useState(0);

  // Team-Heute / Team-Woche (nur fuer Mitarbeiter relevant)
  const [teamWeek, setTeamWeek] = useState({ absences: [], today_summary: null });

  // SSE Live-Updates (vorher WebSocket; SSE laeuft sauber durch Ingress)
  const { attendance: liveAttendance, lastEvent, connected: wsConnected } = useDashboardSSE(canViewTeam);

  // Aktualisierte Anwesenheit: SSE überschreibt REST-Daten
  const currentAttendance = liveAttendance || stats?.attendance;

  useEffect(() => {
    loadStats();
    loadTimeStatus();
    loadChangeRequestsCount();
    if (isAdmin) loadConsistencyCheck();
    if (!canViewTeam) loadTeamWeek();
    // Fallback-Polling wenn SSE nicht verbunden (alle 30s)
    const interval = setInterval(() => {
      if (!wsConnected) {
        loadStats();
      }
      loadTimeStatus();
    }, 30000);
    return () => clearInterval(interval);
  }, [wsConnected, isAdmin, canViewTeam]);
  
  const loadMyShifts = useCallback(async () => {
    setShiftsLoading(true);
    try {
      const weekStart = format(shiftsWeek, 'yyyy-MM-dd');
      const weekEnd = format(endOfWeek(shiftsWeek, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      const response = await dashboardAPI.getMyShifts(weekStart, weekEnd);
      setMyShifts(response.data || []);
    } catch (error) {
      console.error('Failed to load my shifts:', error);
    } finally {
      setShiftsLoading(false);
    }
  }, [shiftsWeek]);

  // Load my shifts when week changes
  useEffect(() => {
    if (!isAdmin) {
      loadMyShifts();
    }
  }, [isAdmin, loadMyShifts]);

  const loadStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTimeStatus = async () => {
    try {
      const response = await timeTrackingAPI.getStatus();
      setTimeStatus(response.data);
    } catch (error) {
      console.error('Failed to load time status:', error);
    }
  };
  
  const loadChangeRequestsCount = async () => {
    try {
      const response = await changeRequestsAPI.getPendingCount();
      setPendingChangeRequests(response.data.count || 0);
    } catch (error) {
      console.error('Failed to load change requests count:', error);
    }
  };

  const loadConsistencyCheck = async () => {
    try {
      const response = await adminAPI.consistencyCheck();
      setConsistencyIssueCount(response.data.total_issues || 0);
    } catch (error) {      console.error('Failed to load consistency check:', error);
    }
  };

  const loadTeamWeek = async () => {
    try {
      const response = await dashboardAPI.getTeamWeekAbsences();
      setTeamWeek({
        absences: response.data?.absences || [],
        today_summary: response.data?.today_summary || null,
      });
    } catch (error) {
      console.error('Failed to load team week summary:', error);
    }
  };

  const handleQuickAction = async (action) => {
    setActionLoading(true);
    try {
      if (action === 'clock-in') {
        await timeTrackingAPI.clockIn();
        toast.success('Eingestempelt');
      } else if (action === 'clock-out') {
        await timeTrackingAPI.clockOut();
        toast.success('Ausgestempelt');
      } else if (action === 'break-start') {
        await timeTrackingAPI.breakStart();
        toast.success('Pause gestartet');
      } else if (action === 'break-end') {
        await timeTrackingAPI.breakEnd();
        toast.success('Pause beendet');
      }
      loadTimeStatus();
      loadStats();  // Aktualisiert Stats nach eigener Aktion (falls WebSocket nicht verbunden)
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Aktion');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'dd.MM.yyyy', { locale: de });
    } catch {
      return dateStr;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-amber-600" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Ausstehend',
      approved: 'Genehmigt',
      rejected: 'Abgelehnt',
      cancelled: 'Storniert',
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={`skel-${i}`} className="h-32 rounded-xl" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in" data-testid="dashboard">
        {/* Header */}
        <div>
          <h1 className="font-heading font-bold text-3xl text-foreground">
            Willkommen, {user?.first_name}!
          </h1>
          <p className="text-muted-foreground mt-1">
            Hier ist Ihre Übersicht für heute
          </p>
        </div>

        {/* Konsistenz-Check Warnung (nur Admin, nur wenn Probleme) */}
        {isAdmin && consistencyIssueCount > 0 && (
          <Card
            className="border-amber-300 bg-amber-50/60 dark:bg-amber-900/10"
            data-testid="consistency-widget"
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-2 rounded-xl bg-amber-100 dark:bg-amber-900/30">
                <Wrench className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-amber-900 dark:text-amber-200">
                  {consistencyIssueCount} Daten-Konsistenzproblem{consistencyIssueCount > 1 ? 'e' : ''} gefunden
                </p>
                <p className="text-xs text-amber-700/80 dark:text-amber-300/80">
                  Mitarbeiter/User-Verknüpfungen oder fehlende Standorte können zu Rechteproblemen führen.
                </p>
              </div>
              <Button size="sm" onClick={() => navigate('/admin/consistency-check')} data-testid="consistency-widget-btn">
                Jetzt prüfen <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Time Tracking Quick Access Card */}
        <TimeTrackingWidget
          timeStatus={timeStatus}
          actionLoading={actionLoading}
          onAction={handleQuickAction}
        />

        {/* „Wartet auf mich"-Kachel: aggregierte Freigaben (versteckt, wenn nichts offen) */}
        <PendingTasksWidget />

        {/* My Shifts Widget (for employees) - first for employees */}
        {!isAdmin && (
          <MyShiftsWidget
            myShifts={myShifts}
            shiftsWeek={shiftsWeek}
            setShiftsWeek={setShiftsWeek}
            shiftsLoading={shiftsLoading}
          />
        )}

        {/* Wichtige News-Beiträge: angepinnt + ungelesene Pflichtbestätigungen */}
        <ImportantNewsWidget />

        {/* QMB / Qualitätsmanagement: offene Pflicht-Lesebestätigungen */}
        <QMBSummaryWidget />

        {/* Fuhrpark-Übersicht: offene Schäden + bald fällige Termine */}
        <FleetSummaryWidget />

        {/* Haustechnik: offene Schadensmeldungen/Aufgaben nach Dringlichkeit */}
        <FacilitySummaryWidget />

        {/* Haustechnik: schlanker Melde-Zugang für Mitarbeiter ohne Board-Zugriff */}
        <MyFacilityReportsWidget />

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {canViewTeam && (
            <>
              <Card className="stat-card" data-testid="stat-employees">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Mitarbeiter</p>
                      <p className="text-3xl font-bold font-heading mt-2">{stats?.total_employees || 0}</p>
                      {stats?.staff_structure && (
                        <p className="text-xs text-muted-foreground mt-1" data-testid="staff-structure">
                          <span className="text-primary font-medium">{stats.staff_structure.operativ}</span> operativ
                          {' · '}
                          <span className="font-medium">{stats.staff_structure.verwaltung}</span> Verwaltung
                        </p>
                      )}
                    </div>
                    <div className="p-3 rounded-xl bg-primary/10">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <Link
                    to="/employees"
                    className="inline-flex items-center text-sm text-primary font-medium mt-4 hover:underline"
                  >
                    Alle anzeigen <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>

              {isAdmin && (
                <Card className="stat-card" data-testid="stat-locations">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Standorte</p>
                        <p className="text-3xl font-bold font-heading mt-2">{stats?.total_locations || 0}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-accent/10">
                        <Building2 className="h-6 w-6 text-accent" />
                      </div>
                    </div>
                    <Link
                      to="/locations"
                      className="inline-flex items-center text-sm text-primary font-medium mt-4 hover:underline"
                    >
                      Verwalten <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          <Card className="stat-card" data-testid="stat-vacations">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {canManageVacations ? 'Offene Anträge' : 'Mein Urlaub'}
                  </p>
                  <p className="text-3xl font-bold font-heading mt-2">{stats?.pending_vacations || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-100">
                  <Calendar className="h-6 w-6 text-amber-600" />
                </div>
              </div>
              <Link
                to={canManageVacations ? '/vacation-requests' : '/vacations'}
                className="inline-flex items-center text-sm text-primary font-medium mt-4 hover:underline"
              >
                {canManageVacations ? 'Bearbeiten' : 'Anzeigen'} <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </CardContent>
          </Card>

          {/* Team-heute Mini-Indikator: nur fuer Mitarbeiter ohne Team-Sicht */}
          {!canViewTeam && teamWeek.today_summary && teamWeek.today_summary.team_size > 0 && (
            <Card className="stat-card" data-testid="stat-team-today">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Team heute</p>
                    <p className="text-3xl font-bold font-heading mt-2 flex items-baseline gap-2">
                      <span className="text-emerald-600">{teamWeek.today_summary.present}</span>
                      <span className="text-base font-normal text-muted-foreground">da</span>
                      <span className="text-muted-foreground/60">·</span>
                      <span className="text-rose-600">{teamWeek.today_summary.absent}</span>
                      <span className="text-base font-normal text-muted-foreground">weg</span>
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-emerald-100">
                    <Users className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Standort-Team ({teamWeek.today_summary.team_size} {teamWeek.today_summary.team_size === 1 ? 'Kollege' : 'Kollegen'})
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Attendance Widget - LIVE */}
          {canViewTeam && currentAttendance && (
            <AttendanceWidget
              currentAttendance={currentAttendance}
              lastEvent={lastEvent}
              wsConnected={wsConnected}
            />
          )}

          {/* Today's Absences Widget */}
          {canViewAbsences && (
            <AbsencesTodayWidget stats={stats} />
          )}

          {/* Pending Corrections Widget */}
          {canViewTimeTracking && (
            <CorrectionsWidget stats={stats} />
          )}

          {/* Pending Change Requests Widget */}
          {canViewTeam && (
            <ChangeRequestsWidget pendingCount={pendingChangeRequests} />
          )}

          {/* Upcoming Holidays Widget */}
          {canViewVacations && stats?.upcoming_holidays?.length > 0 && (
            <HolidaysWidget holidays={stats.upcoming_holidays} />
          )}

          {/* Birthdays Widget */}
          <BirthdaysWidget />

          {/* Bald ablaufende Verträge (HR/Leitung) */}
          {canViewTeam && <ExpiringContractsWidget />}

          {/* Wartet auf Unterschrift (HR/Leitung) */}
          {canViewTeam && <PendingSignaturesWidget />}

          {/* Upcoming Vacations */}
          <Card className="border-border/60">
            <CardHeader className="border-b border-border/40 pb-4">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  Kommender Urlaub
                </CardTitle>
                <Link
                  to="/urlaub?tab=team"
                  className="text-sm text-primary hover:underline inline-flex items-center whitespace-nowrap"
                  data-testid="upcoming-vacations-all-link"
                >
                  Alle ansehen <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {stats?.upcoming_vacations?.length > 0 ? (
                <div className="space-y-4">
                  {stats.upcoming_vacations.map((vacation, index) => (
                    <div
                      key={vacation.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer animate-slide-up"
                      style={{ animationDelay: `${index * 0.05}s` }}
                      onClick={() => navigate(`/urlaub?tab=team&date=${vacation.start_date}&emp=${vacation.employee_id}`)}
                      data-testid={`upcoming-vac-row-${vacation.id}`}
                    >
                      <div>
                        <div className="font-medium">
                          <EmployeeHoverCard employeeId={vacation.employee_id}>
                            <span
                              className="cursor-pointer hover:text-primary hover:underline"
                              data-testid={`upcoming-vac-name-${vacation.employee_id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {vacation.employee_name}
                            </span>
                          </EmployeeHoverCard>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(vacation.start_date)} - {formatDate(vacation.end_date)}
                        </p>
                      </div>
                      <Badge variant="secondary" className="badge-success">
                        {vacation.days_count} Tage
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Keine kommenden Urlaubstage</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Requests (for Managers/Admins) */}
          {canViewTeam && (
            <PendingRequestsWidget
              requests={stats?.recent_requests}
              formatDate={formatDate}
              getStatusIcon={getStatusIcon}
              getStatusLabel={getStatusLabel}
            />
          )}
        </div>

        {/* Quick Actions */}
        <Card className="border-border/60">
          <CardHeader className="border-b border-border/40 pb-4">
            <CardTitle className="font-heading text-lg">Schnellaktionen</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-3">
              <Link to="/vacations">
                <Button
                  variant="outline"
                  className="rounded-full"
                  data-testid="quick-action-vacation"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  Urlaub beantragen
                </Button>
              </Link>
              {canViewTeam && (
                <>
                  <Link to="/employees/new">
                    <Button
                      variant="outline"
                      className="rounded-full"
                      data-testid="quick-action-employee"
                    >
                      <Users className="mr-2 h-4 w-4" />
                      Mitarbeiter anlegen
                    </Button>
                  </Link>
                  {canViewShiftsManager && (
                  <Link to="/shifts">
                    <Button
                      variant="outline"
                      className="rounded-full"
                      data-testid="quick-action-shift"
                    >
                      <CalendarDays className="mr-2 h-4 w-4" />
                      Schicht planen
                    </Button>
                  </Link>
                  )}
                </>
              )}
              {isAdmin && (
                <Link to="/locations/new">
                  <Button
                    variant="outline"
                    className="rounded-full"
                    data-testid="quick-action-location"
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    Standort anlegen
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
